import React from 'react';
import { Loader2, ArrowDown } from 'lucide-react';
import { isMobileContainer } from '../../utils/device';
import usePullToRefresh from '../../hooks/usePullToRefresh';

/**
 * Mobile-only pull-down-to-refresh wrapper. Desktop returns children unchanged.
 */
export default function PullToRefresh({ children, onRefresh, enabled }) {
  const isMobile = enabled ?? isMobileContainer();
  const { pullDistance, refreshing, threshold } = usePullToRefresh({
    enabled: isMobile && typeof onRefresh === 'function',
    onRefresh,
  });

  if (!isMobile) {
    return children;
  }

  const visible = pullDistance > 4 || refreshing;
  const ready = pullDistance >= threshold || refreshing;
  const indicatorHeight = refreshing ? threshold : pullDistance;

  return (
    <div
      className="relative"
      style={
        pullDistance > 0 || refreshing
          ? { overscrollBehaviorY: 'contain' }
          : undefined
      }
    >
      <div
        aria-hidden={!visible}
        className="pointer-events-none flex items-center justify-center overflow-hidden transition-[height] duration-150 ease-out"
        style={{ height: visible ? indicatorHeight : 0 }}
      >
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm ${
            refreshing || ready ? 'text-primary' : ''
          }`}
        >
          {refreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ArrowDown
              className="h-4 w-4 transition-transform duration-150"
              style={{ transform: ready ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          )}
        </div>
      </div>
      <div
        style={{
          transform: pullDistance > 0 && !refreshing ? `translateY(${Math.min(pullDistance * 0.15, 12)}px)` : undefined,
          transition: pullDistance === 0 ? 'transform 0.2s ease-out' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}
